import { useState, useEffect, useMemo, useCallback } from 'react'
import { Avatar } from '@mui/material'
import { reportsService } from '../../../../services'
import { DEFAULT_ROWS } from '../../constants'
import { fmtAmount, fmtDate } from '../../formatters'
import ArticleCodeCell from '../../ArticleCodeCell'

function getErrorMessage(error, fallback) {
  const message =
    error?.response?.data?.message ||
    error?.message ||
    fallback
  return typeof message === 'string' ? message : fallback
}

export function useProductTable({
  enabled,
  salesFilterParams,
  sortBy,
  sortOrder,
  onSort,
  resetPaginationKey,
  onError,
}) {
  const [state, setState] = useState({
    items: [],
    pagination: { total: 0, page: 1, limit: DEFAULT_ROWS },
    loading: false,
  })

  useEffect(() => {
    if (!resetPaginationKey) return
    setState((s) =>
      s.pagination.page === 1
        ? s
        : { ...s, pagination: { ...s.pagination, page: 1 } },
    )
  }, [resetPaginationKey])

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    const run = async () => {
      setState((s) => ({ ...s, loading: true }))
      try {
        const res = await reportsService.getSalesProductWiseReport({
          ...salesFilterParams,
          page: state.pagination.page,
          limit: state.pagination.limit,
        })
        if (!cancelled) {
          setState((s) => ({
            ...s,
            items: res.items || [],
            pagination: res.pagination || s.pagination,
            loading: false,
          }))
        }
      } catch (error) {
        console.error('Failed to load product-wise sales:', error)
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            items: s.items.length ? s.items : [],
          }))
          onError?.(getErrorMessage(error, 'Could not load product-wise sales.'))
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    enabled,
    salesFilterParams,
    state.pagination.page,
    state.pagination.limit,
    onError,
  ])

  const setPage = useCallback((page) => {
    setState((s) => ({
      ...s,
      pagination: { ...s.pagination, page },
    }))
  }, [])

  const setLimit = useCallback((limit) => {
    setState((s) => ({
      ...s,
      pagination: { ...s.pagination, page: 1, limit },
    }))
  }, [])

  const columns = useMemo(
    () => [
      {
        id: 'articleCode',
        header: 'Article Code',
        sortField: 'articleCode',
        renderCell: (row) => <ArticleCodeCell articleCode={row.articleCode} />,
      },
      {
        id: 'image',
        header: 'Image',
        renderCell: (row) => (
          <Avatar
            variant="rounded"
            src={row.imageThumbnail || undefined}
            alt={row.productName || 'Product'}
            sx={{ width: 32, height: 32, bgcolor: '#f3f4f6', fontSize: 12 }}
          />
        ),
      },
      { id: 'productName', header: 'Product', sortField: 'productName' },
      { id: 'orgName', header: 'Shop', sortField: 'orgName' },
      {
        id: 'totalQuantitySold',
        header: 'Qty Sold',
        sortField: 'totalQuantitySold',
      },
      {
        id: 'totalSalesAmount',
        header: 'Total Sales',
        sortField: 'totalSalesAmount',
      },
      { id: 'invoiceCount', header: 'Invoices', sortField: 'invoiceCount' },
      { id: 'lastSoldAt', header: 'Last Sold', sortField: 'lastSoldAt' },
      { id: 'nonShwapno', header: 'Non Shwapno' },
    ],
    [],
  )

  const rows = useMemo(
    () =>
      state.items.map((item) => ({
        ...item,
        totalSalesAmount: fmtAmount(item.totalSalesAmount),
        lastSoldAt: fmtDate(item.lastSoldAt),
        nonShwapno: item.isNonShwapno ? 'Yes' : 'No',
      })),
    [state.items],
  )

  return {
    items: state.items,
    rows,
    columns,
    loading: state.loading,
    pagination: state.pagination,
    setPage,
    setLimit,
    sortBy,
    sortOrder,
    onSort,
  }
}
